import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  FileText,
  Search,
  Plus,
  Calendar,
  Building2,
  Users,
  TrendingUp,
  Filter,
  Download,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function SalesQuotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    fetchQuotes();
  }, [statusFilter]);

  const fetchQuotes = async () => {
    try {
      const url = statusFilter 
        ? `${API}/api/sales/quotes?status=${statusFilter}`
        : `${API}/api/sales/quotes`;
      
      const res = await fetch(url, { credentials: "include" });
      const data = await res.json();
      
      if (data.success) {
        setQuotes(data.quotes);
      }
    } catch (error) {
      console.error("Failed to fetch quotes:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: "bg-gray-100 text-gray-700",
      sent: "bg-blue-100 text-blue-700",
      accepted: "bg-green-100 text-green-700",
      rejected: "bg-red-100 text-red-700",
      expired: "bg-orange-100 text-orange-700",
    };
    return colors[status] || "bg-gray-100 text-gray-700";
  };

  const getMarginColor = (margin) => {
    if (margin >= 70) return "text-green-600";
    if (margin >= 50) return "text-green-600";
    if (margin >= 30) return "text-yellow-600";
    return "text-red-600";
  };

  const handleDownloadPDF = async (quoteId, schoolName) => {
    try {
      const res = await fetch(`${API}/api/sales/quotes/${quoteId}/pdf`, {
        credentials: "include",
      });

      if (!res.ok) {
        throw new Error("Failed to download PDF");
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Eduzy_Quote_${quoteId}_${schoolName.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded!");
    } catch (error) {
      console.error("PDF download failed:", error);
      toast.error("Failed to download PDF");
    }
  };

  const filteredQuotes = quotes.filter(quote =>
    quote.school_name?.toLowerCase().includes(search.toLowerCase()) ||
    quote.id?.toLowerCase().includes(search.toLowerCase())
  );

  const statuses = ["draft", "sent", "accepted", "rejected", "expired"];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quotes</h1>
          <p className="text-gray-500">{quotes.length} total quotes</p>
        </div>
        <Button
          onClick={() => navigate("/sales/calculator")}
          className="bg-gradient-to-r from-blue-600 to-purple-600"
          data-testid="new-quote-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Quote
        </Button>
      </div>

      {/* Filters */}
      <Card className="bg-white shadow-sm border-gray-200">
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search by school name or quote ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-gray-50"
                  data-testid="search-quotes-input"
                />
              </div>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button
                variant={statusFilter === "" ? "default" : "outline"}
                size="sm"
                onClick={() => setStatusFilter("")}
                className={statusFilter === "" ? "bg-blue-600" : ""}
              >
                All
              </Button>
              {statuses.map((status) => (
                <Button
                  key={status}
                  variant={statusFilter === status ? "default" : "outline"}
                  size="sm"
                  onClick={() => setStatusFilter(status)}
                  className={statusFilter === status ? "bg-blue-600" : ""}
                >
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quotes List */}
      {filteredQuotes.length === 0 ? (
        <Card className="bg-white shadow-sm border-gray-200">
          <CardContent className="py-12 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No quotes found</h3>
            <p className="text-gray-500 mb-4">
              {search || statusFilter ? "Try adjusting your filters" : "Create your first quote to get started"}
            </p>
            <Button onClick={() => navigate("/sales/calculator")}>
              <Plus className="w-4 h-4 mr-2" />
              Create Quote
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredQuotes.map((quote) => (
            <Card 
              key={quote.id}
              className="bg-white shadow-sm border-gray-200 hover:shadow-md transition-shadow"
            >
              <CardContent className="py-4">
                <div className="flex flex-wrap items-center justify-between gap-4">
                  {/* Quote Info */}
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-3 mb-2">
                      <Badge className={getStatusColor(quote.status)}>
                        {quote.status}
                      </Badge>
                      <span className="text-sm text-gray-500 font-mono">{quote.id}</span>
                    </div>
                    <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      {quote.school_name}
                    </h3>
                    <p className="text-sm text-gray-500">{quote.contact_email}</p>
                  </div>

                  {/* Quote Details */}
                  <div className="flex items-center gap-6 text-sm">
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-gray-500">
                        <Users className="w-4 h-4" />
                        <span>Students</span>
                      </div>
                      <p className="font-semibold text-gray-900">{quote.students}</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-gray-500">
                        <Calendar className="w-4 h-4" />
                        <span>Duration</span>
                      </div>
                      <p className="font-semibold text-gray-900">{quote.duration_months} mo</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center gap-1 text-gray-500">
                        <TrendingUp className="w-4 h-4" />
                        <span>Margin</span>
                      </div>
                      <p className={`font-semibold ${getMarginColor(quote.margin_percent)}`}>
                        {quote.margin_percent?.toFixed(1)}%
                      </p>
                    </div>
                  </div>

                  {/* Amount */}
                  <div className="text-right min-w-[150px]">
                    <p className="text-sm text-gray-500">Total Amount</p>
                    <p className="text-xl font-bold text-gray-900">
                      {formatCurrency(quote.total_amount)}
                    </p>
                    <p className="text-xs text-orange-600">+ 18% GST</p>
                  </div>
                </div>

                {/* Footer */}
                <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between text-sm">
                  <div className="text-gray-500">
                    Created {new Date(quote.created_at).toLocaleDateString()} by {quote.created_by_name}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadPDF(quote.id, quote.school_name)}
                    >
                      <Download className="w-4 h-4 mr-1" />
                      PDF
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {/* TODO: View quote details */}}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
